import { SelectPicker } from "rsuite";
import { Input } from "reactstrap";

import { BaseVariableProps, RecordDynamic } from "../EmailBuilderTypes";
import ModulePicker from "../../../Modules/ModulePicker";
import FieldUtils from "../../../../utils/FieldUtils";
import useModule from "../../../../hooks/useModule";
import VariableCollapse from "./VariableCollapse";

const RecordDynamicBuilder = ({
  variable,
  setVariables,
  index,
  customVariables,
  expandAll,
}: BaseVariableProps) => {
  const isRecordDynamic = variable.Type === "RecordDynamic";
  const { module } = useModule(isRecordDynamic ? variable.ModuleAPIName : "");

  if (!isRecordDynamic) return null;

  const moduleAPIName = variable.ModuleAPIName;
  const fieldAPIName = GetFieldAPIName(variable.FieldAPIName);

  const Builder = (
    <div className="pb-4 pt-2 px-4">
      <h4>Data Source</h4>
      <ModulePicker
        cleanable={false}
        value={variable.ModuleAPIName}
        onChange={(newModule: string | null) => {
          const newMod = newModule || "";
          updateVariable("ModuleAPIName", newMod);
        }}
        omitGlobalModules={true}
      />
      <h4 className="mt-4">Field</h4>
      <SelectPicker
        block
        preventOverflow
        cleanable={false}
        placeholder={
          moduleAPIName ? "Select Field" : "Select a data source first!"
        }
        disabled={!moduleAPIName}
        value={fieldAPIName}
        groupBy="group"
        data={
          module?.Fields?.flatMap?.((f) => {
            if (FieldUtils.fieldTypeIsSubitem(f?.FieldType)) {
              return [
                ...(f.SubitemFields?.map((sF) => ({
                  label: `${f?.Name} ${sF?.Name}`,
                  value: `${f.APIName}.${sF?.APIName}`,
                  type: f?.FieldType,
                  group: f.Name,
                })) || []),
                {
                  label: f?.Name,
                  value: f?.APIName,
                  type: f?.FieldType,
                  group: "Non Subitem Fields",
                },
              ];
            }

            return [
              {
                label: f?.Name,
                value: f?.APIName,
                type: f?.FieldType,
                group: "Non Subitem Fields",
              },
            ];
          }) || []
        }
        onSelect={(fieldAPIName, fieldObj) => {
          const fieldType = fieldObj?.type;

          if (!fieldAPIName || !fieldType) return;

          setVariables((prev) => {
            return prev.map((variable, i) => {
              if (i !== index) return variable;

              return {
                ...variable,
                FieldAPIName: fieldAPIName,
                FieldType: fieldType,
              };
            });
          });
        }}
      />
      {/* Allows user to select the subitem index they want to grab the data from. */}
      {/* Requires some messier code to support FieldAPIName structure of Field.SubitemField.Index */}
      {variable?.FieldAPIName?.split(".")?.length > 1 && (
        <>
          <h4 className="mt-4">Subitem Index</h4>
          <Input
            type="number"
            bsSize="sm"
            placeholder="Ex: 0"
            value={variable?.FieldAPIName?.split?.(".")?.[2] || ""}
            onChange={(e) =>
              setVariables((prev) => {
                return prev.map((v, i) => {
                  if (i !== index) return v;

                  const splitVariable = variable?.FieldAPIName?.split?.(".");
                  const newIndex = e.target.value;
                  const baseAPIName = `${splitVariable[0]}.${splitVariable[1]}`;

                  if (!newIndex) return { ...v, FieldAPIName: baseAPIName };

                  return {
                    ...v,
                    FieldAPIName: `${baseAPIName}.${newIndex}`,
                  };
                });
              })
            }
          />
        </>
      )}
    </div>
  );

  return (
    <VariableCollapse
      variable={variable}
      setVariables={setVariables}
      index={index}
      customVariables={customVariables}
      expandAll={expandAll}
    >
      {Builder}
    </VariableCollapse>
  );

  function updateVariable(key: keyof RecordDynamic, value: string) {
    setVariables((prev) => {
      return prev.map((variable, i) => {
        if (i !== index) return variable;

        return { ...variable, [key]: value };
      });
    });
  }
};

export default RecordDynamicBuilder;

function GetFieldAPIName(fieldAPIName: string) {
  const split = fieldAPIName?.split?.(".");

  if (split.length < 2) return fieldAPIName;

  if (split.length === 2) return fieldAPIName;

  if (split.length === 3) return `${split[0]}.${split[1]}`;
}
